import moment from "moment";
import AgeRestrictionDropdown from "components/dropdowns/reactselect/AgeRestrictionDropdown";
import { FormSection } from "components/MainSectionTemplate/types";
import OptionalTimePicker from "components/OptionalTimePicker";
import VenueModalPicker from "components/Picker/VenuePicker/VenueModalPicker";
import OptionalNumberInput from "components/ValidatedFields/OptionalNumberInput";
import React from "react";
import ReadonlyVenue from "show/detail/components/ReadonlyVenue";
import ReadonlyContact from "show/detail/components/ReadonlyContact";
import { fetchFullContact, getInstructionForInternal, ShowInfo } from "show/detail/utils";
import { getAgeRestriction } from "utilities/enumutils";
import ContactModalPicker from "components/Picker/ContactPicker/ContactModalPicker";

export const venueSection = (
  show: server.dto.Show,
  showInfo: ShowInfo,
  venueContact: server.dto.Contact,
  isExternal: boolean,
  isSidebar: boolean
): FormSection => {
  const isInternalUser = !isExternal;
  const ageRestriction = showInfo.ageRestriction ? getAgeRestriction(showInfo.ageRestriction) : null;

  const section: FormSection = {
    type: "regular",
    key: "showVenueSection",
    title: site.currentEnvironment.functionGroups.venue.singular,
    hidden: !site.currentEnvironment.functionGroups.venue.enabled,
    fields: [{
      key: "venue",
      label: site.currentEnvironment.functionGroups.venue.singular,
      isEmpty: !showInfo.venue,
      render: () => <ReadonlyVenue venue={showInfo.venue} displayTooltip={isInternalUser} />,
      inPageEdit: {
        hoverEdit: true,
        canRemove: true,
        initialValue: showInfo.venue,
        fullWidth: true,
        render: ({ onChange, cancel, value }) => (
          <div className="main-section-template-fields-padding">
            <VenueModalPicker
              inPageEditing={{ onChange, cancel, hasValue: Boolean(value) }}
              canCreateNew={!isSidebar && site.permissions.addressBook.canCreate}
              afterCreate={isSidebar ? "goBack" : "closeSidebar"}
            />
          </div>
        ),
        type: "custom",
        submitChange: async (value: server.dto.VenueSmall) => {
          if (value == null) {
            return window.CoreApi.Show.UpdateForInternalEnvironment(
              getInstructionForInternal(show.showId, showInfo, {
                venueCompanyId: null,
                venueContactId: null,
              })
            );
          }
          const venue = await window.CoreApi.Venue.GetVenue(value.venueId);
          const { details } = venue.data.data;

          return window.CoreApi.Show.UpdateForInternalEnvironment(
            getInstructionForInternal(show.showId, showInfo, {
              venueCompanyId: value?.venueId ?? null,
              venueContactId: null,
              capacity: details.capacity,
              ageRestriction: details.ageRestriction,
              doors: {
                open: details.doors?.open?.cultureInvariant,
                close: details.doors?.close?.cultureInvariant,
              }

            })
          )
        }
      }
    }, {
      key: "venueContact",
      hidden: showInfo.venue == null,
      label: RESX.Contact.resxContact,
      isEmpty: !venueContact,
      render: () => <ReadonlyContact contact={venueContact} displayDetails displayPreview={isInternalUser} />,
      inPageEdit: {
        initialValue: venueContact,
        fullWidth: true,
        hoverEdit: true,
        canRemove: true,
        render: ({ onChange, cancel, value }) => (
          <div className="main-section-template-fields-padding">
            <ContactModalPicker
              inPageEditing={{
                onChange: async (picked: server.dto.ContactSmallV2, submit: boolean) => {
                  const full = await fetchFullContact(picked);
                  onChange(full, submit);
                },
                cancel,
                hasValue: Boolean(value),
              }}
              fromCompanyId={showInfo.venue?.venueId}
              canCreateNew={!isSidebar && site.permissions.addressBook.canCreate}
              afterCreate={isSidebar ? "goBack" : "closeSidebar"}
            />
          </div>
        ),
        type: "custom",
        submitChange: (value: server.dto.Contact) =>
          window.CoreApi.Show.UpdateForInternalEnvironment(
            getInstructionForInternal(show.showId, showInfo, { venueContactId: value?.contactId ?? null })
          )
      }
    }, {
      key: "capacity",
      hidden: showInfo.venue == null,
      label: RESX.Venue.Capacity,
      isEmpty: !showInfo.capacity,
      render: () => <>{showInfo.capacity} PAX</>,
      inPageEdit: {
        initialValue: showInfo.capacity,
        render: ({ value, onChange, cancel }) => (
          <OptionalNumberInput
            autoFocus
            value={value}
            onChange={onChange}
            onEnterPressed={v => onChange(v, true)}
            onEscPressed={cancel}
            minValue={0}
            maxValue={serverReference.constants.venue.maxCapacity}
            canBeEmpty
            decimalPoints={0}
            labelContent="PAX"
            width={100}
          />
        ),
        type: "confirmOrCancelButton",
        submitChange: (value: number) =>
          window.CoreApi.Show.UpdateForInternalEnvironment(
            getInstructionForInternal(show.showId, showInfo, { capacity: value ?? 0 })
          )
      }
    }, {
      key: "ageRestriction",
      hidden: showInfo.venue == null,
      label: RESX.Venue.AgeRestrictions,
      isEmpty: !ageRestriction || ageRestriction.value === "unknown",
      render: () => <>{ageRestriction?.text}</>,
      inPageEdit: {
        initialValue: ageRestriction,
        fullWidth: true,
        render: ({ value, onChange }) => (
          <AgeRestrictionDropdown
            value={value}
            onChange={onChange}
            config={{
              focusOnMount: true,
              openMenuOnFocus: true,
            }}
          />
        ),
        type: "confirmOrCancelButton",
        submitChange: (value: server.dto.AgeRestriction) =>
          window.CoreApi.Show.UpdateForInternalEnvironment(
            getInstructionForInternal(show.showId, showInfo, { ageRestriction: value.value })
          )
      }
    }, {
      key: "doorsOpen",
      hidden: showInfo.venue == null,
      label: RESX.TimetableEvent.DoorsOpen,
      isEmpty: !showInfo.doorsOpen,
      render: () => <>{showInfo.doorsOpen?.format("HH:mm")}</>,
      inPageEdit: {
        initialValue: showInfo.doorsOpen,
        render: ({ value, onChange }) => (
          <OptionalTimePicker
            value={value}
            onChange={onChange}
            autoFocus
          />
        ),
        type: "confirmOrCancelButton",
        submitChange: (value: moment.Moment) =>
          window.CoreApi.Show.UpdateForInternalEnvironment(
            getInstructionForInternal(show.showId, showInfo, {
              doors: {
                open: value?.format("HH:mm") ?? null,
                close: showInfo.doorsClose?.format("HH:mm") ?? null,
              }
            })
          )
      }
    }, {
      key: "doorsClose",
      hidden: showInfo.venue == null,
      label: RESX.TimetableEvent.DoorsClose,
      isEmpty: !showInfo.doorsClose,
      render: () => <>{showInfo.doorsClose?.format("HH:mm")}</>,
      inPageEdit: {
        initialValue: showInfo.doorsClose,
        render: ({ value, onChange }) => (
          <OptionalTimePicker
            value={value}
            onChange={onChange}
            autoFocus
          />
        ),
        type: "confirmOrCancelButton",
        submitChange: (value: moment.Moment) =>
          window.CoreApi.Show.UpdateForInternalEnvironment(
            getInstructionForInternal(show.showId, showInfo, {
              doors: {
                open: showInfo.doorsOpen?.format("HH:mm") ?? null,
                close: value?.format("HH:mm") ?? null,
              }
            })
          )
      }
    }]
  };

  if (isExternal) {
    section.fields = section.fields.map(f => ({ ...f, editDisabled: true }));
  }
  return section;
};
